/**
 * Career Progression Timeline Data
 *
 * Defines career ladders and progression paths for each career:
 * - Entry-level to senior positions
 * - Typical timeframes for advancement
 * - Skills required at each level
 * - Salary progression
 * - Alternative paths and lateral moves
 */

import { CareerPath } from './careerPaths';

export interface CareerLevel {
  title: string;
  yearsExperience: string;
  salaryRange: {
    min: number;
    max: number;
  };
  keyResponsibilities: string[];
  skillsRequired: string[];
  typicalTimeToReach: string;
  certifications?: string[];
}

export interface CareerProgression {
  careerId: string;
  levels: CareerLevel[];
  alternativePaths: AlternativePath[];
  keyMilestones: Milestone[];
  growthTips: string[];
}

export interface AlternativePath {
  title: string;
  description: string;
  fromLevel: number; // Index of the level to branch from
  requirements: string[];
}

export interface Milestone {
  title: string;
  timing: string;
  description: string;
  icon: string;
}

// Career progression data
const CAREER_PROGRESSIONS: Record<string, CareerProgression> = {
  web_developer: {
    careerId: 'web_developer',
    levels: [
      {
        title: 'Junior Developer',
        yearsExperience: '0-2 years',
        salaryRange: { min: 50000, max: 75000 },
        keyResponsibilities: [
          'Implement features under supervision',
          'Fix bugs and write unit tests',
          'Learn codebase and development practices',
          'Participate in code reviews (as reviewee)',
        ],
        skillsRequired: ['HTML/CSS', 'JavaScript basics', 'Git', 'One framework (React/Vue/Angular)'],
        typicalTimeToReach: 'Entry point',
      },
      {
        title: 'Mid-Level Developer',
        yearsExperience: '2-5 years',
        salaryRange: { min: 75000, max: 110000 },
        keyResponsibilities: [
          'Own features end-to-end',
          'Mentor junior developers',
          'Contribute to technical decisions',
          'Lead code reviews',
        ],
        skillsRequired: ['Deep JavaScript knowledge', 'Multiple frameworks', 'Backend basics', 'Testing strategies', 'API design'],
        typicalTimeToReach: '2-3 years',
      },
      {
        title: 'Senior Developer',
        yearsExperience: '5-8 years',
        salaryRange: { min: 110000, max: 150000 },
        keyResponsibilities: [
          'Lead technical projects',
          'Define architecture decisions',
          'Set coding standards',
          'Cross-team collaboration',
        ],
        skillsRequired: ['System design', 'Performance optimization', 'Security best practices', 'Team leadership'],
        typicalTimeToReach: '4-6 years',
      },
      {
        title: 'Staff/Principal Engineer',
        yearsExperience: '8+ years',
        salaryRange: { min: 150000, max: 220000 },
        keyResponsibilities: [
          'Company-wide technical strategy',
          'Solve the hardest problems',
          'Define multi-year roadmaps',
          'Industry thought leadership',
        ],
        skillsRequired: ['Organizational influence', 'Strategic thinking', 'Cross-functional leadership', 'Innovation'],
        typicalTimeToReach: '8-12 years',
      },
    ],
    alternativePaths: [
      {
        title: 'Engineering Manager',
        description: 'Lead teams of engineers, focus on people and process',
        fromLevel: 2, // From Senior Developer
        requirements: ['Leadership skills', 'Project management', 'Hiring experience', 'People development'],
      },
      {
        title: 'Product Engineer',
        description: 'Hybrid role blending development with product strategy',
        fromLevel: 1, // From Mid-Level
        requirements: ['Product thinking', 'User research', 'Business acumen', 'Communication'],
      },
      {
        title: 'DevOps/Platform Engineer',
        description: 'Focus on infrastructure, CI/CD, and developer tooling',
        fromLevel: 1, // From Mid-Level
        requirements: ['Cloud platforms (AWS/GCP/Azure)', 'Docker/Kubernetes', 'Automation', 'Infrastructure as code'],
      },
    ],
    keyMilestones: [
      { title: 'First Deployed Project', timing: 'Month 3-6', description: 'Ship code to production that real users interact with', icon: '🚀' },
      { title: 'First Code Review Given', timing: 'Year 1', description: 'Start contributing to team code quality', icon: '👀' },
      { title: 'Lead First Project', timing: 'Year 2-3', description: 'Own a feature from planning to deployment', icon: '👑' },
      { title: 'First Conference Talk', timing: 'Year 4-6', description: 'Share knowledge with the broader community', icon: '🎤' },
    ],
    growthTips: [
      'Build side projects to experiment with new technologies',
      'Contribute to open source to gain visibility and learn from others',
      'Write technical blog posts to clarify your thinking',
      'Seek out the hardest problems—that\'s where growth happens',
      'Find a mentor who\'s 2-3 levels ahead of where you want to be',
    ],
  },

  customer_success_specialist: {
    careerId: 'customer_success_specialist',
    levels: [
      {
        title: 'Customer Success Associate',
        yearsExperience: '0-1 years',
        salaryRange: { min: 45000, max: 60000 },
        keyResponsibilities: [
          'Handle customer onboarding',
          'Respond to support inquiries',
          'Update CRM records',
          'Learn product inside and out',
        ],
        skillsRequired: ['Communication', 'CRM tools', 'Product knowledge', 'Empathy'],
        typicalTimeToReach: 'Entry point',
      },
      {
        title: 'Customer Success Specialist',
        yearsExperience: '1-3 years',
        salaryRange: { min: 55000, max: 80000 },
        keyResponsibilities: [
          'Manage portfolio of accounts',
          'Conduct QBRs and check-ins',
          'Drive product adoption',
          'Identify upsell opportunities',
        ],
        skillsRequired: ['Account management', 'Data analysis', 'Presentation skills', 'Problem-solving'],
        typicalTimeToReach: '1 year',
      },
      {
        title: 'Senior Customer Success Manager',
        yearsExperience: '3-5 years',
        salaryRange: { min: 80000, max: 120000 },
        keyResponsibilities: [
          'Manage strategic/enterprise accounts',
          'Mentor junior CSMs',
          'Contribute to CS playbooks',
          'Hit retention and expansion targets',
        ],
        skillsRequired: ['Strategic thinking', 'Executive communication', 'Mentorship', 'Process improvement'],
        typicalTimeToReach: '3-4 years',
      },
      {
        title: 'Director of Customer Success',
        yearsExperience: '5-8 years',
        salaryRange: { min: 120000, max: 180000 },
        keyResponsibilities: [
          'Lead CS team',
          'Define CS strategy',
          'Own department metrics',
          'Cross-functional leadership',
        ],
        skillsRequired: ['Team leadership', 'Strategy development', 'Budget management', 'Hiring'],
        typicalTimeToReach: '5-7 years',
      },
    ],
    alternativePaths: [
      {
        title: 'Sales/Account Executive',
        description: 'Transition to a quota-carrying sales role',
        fromLevel: 1, // From Specialist
        requirements: ['Sales skills', 'Prospecting', 'Closing techniques', 'Pipeline management'],
      },
      {
        title: 'Product Manager',
        description: 'Leverage customer insights to drive product strategy',
        fromLevel: 2, // From Senior CSM
        requirements: ['Product thinking', 'Roadmap planning', 'Technical aptitude', 'Stakeholder management'],
      },
      {
        title: 'Customer Success Operations',
        description: 'Focus on tools, data, and scaling CS processes',
        fromLevel: 1, // From Specialist
        requirements: ['Data analysis', 'Tool administration', 'Process design', 'Automation'],
      },
    ],
    keyMilestones: [
      { title: 'First Renewal Won', timing: 'Month 3-6', description: 'Successfully renew your first customer contract', icon: '🔄' },
      { title: 'First Upsell/Expansion', timing: 'Year 1', description: 'Grow revenue within an existing account', icon: '📈' },
      { title: 'Save an At-Risk Account', timing: 'Year 1-2', description: 'Turn around a customer who was about to churn', icon: '🦸' },
      { title: 'Lead Your First QBR', timing: 'Year 1', description: 'Present quarterly business review to executives', icon: '📊' },
    ],
    growthTips: [
      'Get certified in your company\'s product and relevant tools (Salesforce, HubSpot, Gainsight)',
      'Learn to read and present data—numbers tell the story',
      'Build relationships across departments (Sales, Product, Support)',
      'Document your wins with metrics for performance reviews',
      'Practice executive communication—brief, value-focused, confident',
    ],
  },

  project_manager: {
    careerId: 'project_manager',
    levels: [
      {
        title: 'Project Coordinator',
        yearsExperience: '0-2 years',
        salaryRange: { min: 45000, max: 60000 },
        keyResponsibilities: [
          'Support project managers',
          'Schedule meetings and track action items',
          'Update project documentation',
          'Communicate status updates',
        ],
        skillsRequired: ['Organization', 'Communication', 'Basic PM tools', 'Attention to detail'],
        typicalTimeToReach: 'Entry point',
      },
      {
        title: 'Project Manager',
        yearsExperience: '2-5 years',
        salaryRange: { min: 65000, max: 95000 },
        keyResponsibilities: [
          'Lead projects end-to-end',
          'Manage budgets and resources',
          'Identify and mitigate risks',
          'Stakeholder management',
        ],
        skillsRequired: ['Planning', 'Risk management', 'Budget tracking', 'Team leadership'],
        typicalTimeToReach: '2 years',
        certifications: ['PMP', 'Scrum Master', 'CAPM'],
      },
      {
        title: 'Senior Project Manager',
        yearsExperience: '5-8 years',
        salaryRange: { min: 95000, max: 130000 },
        keyResponsibilities: [
          'Lead complex/strategic projects',
          'Mentor junior PMs',
          'Define PM best practices',
          'Executive reporting',
        ],
        skillsRequired: ['Strategic planning', 'Executive communication', 'Mentorship', 'Complex stakeholder management'],
        typicalTimeToReach: '5 years',
        certifications: ['PMP', 'PgMP', 'Agile certifications'],
      },
      {
        title: 'Program/Portfolio Manager',
        yearsExperience: '8+ years',
        salaryRange: { min: 130000, max: 180000 },
        keyResponsibilities: [
          'Oversee multiple projects/programs',
          'Strategic resource allocation',
          'Organizational change leadership',
          'C-suite partnerships',
        ],
        skillsRequired: ['Program management', 'Organizational leadership', 'Strategic thinking', 'Change management'],
        typicalTimeToReach: '8-10 years',
      },
    ],
    alternativePaths: [
      {
        title: 'Product Manager',
        description: 'Shift from execution to defining what gets built',
        fromLevel: 1, // From PM
        requirements: ['Product thinking', 'User research', 'Business strategy', 'Technical fluency'],
      },
      {
        title: 'Scrum Master/Agile Coach',
        description: 'Specialize in Agile methodologies and team facilitation',
        fromLevel: 1, // From PM
        requirements: ['Agile expertise', 'Coaching skills', 'Facilitation', 'Continuous improvement'],
      },
      {
        title: 'Operations Manager',
        description: 'Apply PM skills to ongoing operations and process improvement',
        fromLevel: 2, // From Senior PM
        requirements: ['Process optimization', 'Operational thinking', 'Team management', 'Metrics'],
      },
    ],
    keyMilestones: [
      { title: 'First Project Delivered', timing: 'Month 3-6', description: 'Complete a project on time and within scope', icon: '✅' },
      { title: 'First Certification', timing: 'Year 1-2', description: 'Earn PMP, Scrum Master, or similar credential', icon: '🎓' },
      { title: 'Lead a Cross-Functional Project', timing: 'Year 2-3', description: 'Manage a project spanning multiple departments', icon: '🌐' },
      { title: 'Recover a Troubled Project', timing: 'Year 3-5', description: 'Turn around a project that was off track', icon: '🔧' },
    ],
    growthTips: [
      'Get certified early—PMP or Scrum Master opens doors',
      'Learn to say no or negotiate scope—protecting your team is key',
      'Build relationships before you need them',
      'Document lessons learned from every project',
      'Stay current with PM tools and methodologies',
    ],
  },

  electrician: {
    careerId: 'electrician',
    levels: [
      {
        title: 'Electrical Apprentice',
        yearsExperience: '0-4 years',
        salaryRange: { min: 30000, max: 50000 },
        keyResponsibilities: [
          'Learn under journeyman supervision',
          'Assist with installations and repairs',
          'Complete required classroom hours',
          'Practice safety protocols',
        ],
        skillsRequired: ['Basic electrical knowledge', 'Math skills', 'Physical fitness', 'Safety awareness'],
        typicalTimeToReach: 'Entry point',
      },
      {
        title: 'Journeyman Electrician',
        yearsExperience: '4-8 years',
        salaryRange: { min: 50000, max: 80000 },
        keyResponsibilities: [
          'Work independently on projects',
          'Read and interpret blueprints',
          'Install and maintain electrical systems',
          'Supervise apprentices',
        ],
        skillsRequired: ['NEC code expertise', 'Troubleshooting', 'Blueprint reading', 'Customer service'],
        typicalTimeToReach: '4-5 years (after apprenticeship)',
        certifications: ['State Journeyman License'],
      },
      {
        title: 'Master Electrician',
        yearsExperience: '8+ years',
        salaryRange: { min: 70000, max: 100000 },
        keyResponsibilities: [
          'Design electrical systems',
          'Pull permits and handle inspections',
          'Train and supervise teams',
          'Business development',
        ],
        skillsRequired: ['Advanced code knowledge', 'System design', 'Leadership', 'Business management'],
        typicalTimeToReach: '6-8 years (after journeyman)',
        certifications: ['State Master License'],
      },
      {
        title: 'Electrical Contractor/Owner',
        yearsExperience: '10+ years',
        salaryRange: { min: 80000, max: 200000 },
        keyResponsibilities: [
          'Run your own business',
          'Bid on contracts',
          'Hire and manage employees',
          'Build client relationships',
        ],
        skillsRequired: ['Business ownership', 'Sales', 'Financial management', 'Networking'],
        typicalTimeToReach: '10+ years',
        certifications: ['Contractor License', 'Business licenses'],
      },
    ],
    alternativePaths: [
      {
        title: 'Electrical Inspector',
        description: 'Review electrical work for code compliance',
        fromLevel: 2, // From Master Electrician
        requirements: ['Deep code knowledge', 'Attention to detail', 'Communication', 'Certification'],
      },
      {
        title: 'Industrial/Maintenance Electrician',
        description: 'Specialize in factory and industrial electrical systems',
        fromLevel: 1, // From Journeyman
        requirements: ['PLC knowledge', 'Motor controls', 'Instrumentation', 'Troubleshooting'],
      },
      {
        title: 'Electrical Estimator',
        description: 'Calculate costs and prepare bids for electrical projects',
        fromLevel: 1, // From Journeyman
        requirements: ['Math skills', 'Blueprint reading', 'Cost analysis', 'Software proficiency'],
      },
    ],
    keyMilestones: [
      { title: 'Start Apprenticeship', timing: 'Year 1', description: 'Enter a registered apprenticeship program', icon: '🛠️' },
      { title: 'First Solo Task', timing: 'Year 1-2', description: 'Complete a task independently (under supervision)', icon: '⚡' },
      { title: 'Pass Journeyman Exam', timing: 'Year 4-5', description: 'Earn your journeyman license', icon: '📜' },
      { title: 'First Job as Lead', timing: 'Year 6-8', description: 'Lead a crew on a job site', icon: '👷' },
    ],
    growthTips: [
      'Complete apprenticeship hours diligently—shortcuts don\'t exist in the trades',
      'Learn the NEC code inside and out',
      'Build relationships with inspectors and general contractors',
      'Consider specializing (solar, industrial, smart home) for higher pay',
      'Start thinking about business ownership early if that\'s your goal',
    ],
  },
};

/**
 * Get career progression data for a specific career
 */
export function getCareerProgression(career: CareerPath): CareerProgression {
  // Return specific progression if available, otherwise generate generic one
  if (CAREER_PROGRESSIONS[career.id]) {
    return CAREER_PROGRESSIONS[career.id];
  }

  // Generate generic progression based on career data
  return generateGenericProgression(career);
}

/**
 * Generate a generic career progression for careers without specific data
 */
function generateGenericProgression(career: CareerPath): CareerProgression {
  const { salary } = career;
  const entryMid = (salary.entry_min + salary.entry_max) / 2;
  const midSenior = entryMid * 1.5;
  const seniorLead = entryMid * 2.2;

  return {
    careerId: career.id,
    levels: [
      {
        title: `Entry-Level ${career.title}`,
        yearsExperience: '0-2 years',
        salaryRange: { min: salary.entry_min, max: salary.entry_max },
        keyResponsibilities: [
          'Learn core skills and processes',
          'Complete assigned tasks',
          'Build foundational knowledge',
          'Receive mentorship',
        ],
        skillsRequired: career.requiredSkills.slice(0, 4),
        typicalTimeToReach: 'Entry point',
      },
      {
        title: `${career.title}`,
        yearsExperience: '2-5 years',
        salaryRange: { min: salary.entry_max, max: Math.round(midSenior) },
        keyResponsibilities: [
          'Work independently',
          'Own projects end-to-end',
          'Mentor newer team members',
          'Contribute to improvements',
        ],
        skillsRequired: career.requiredSkills,
        typicalTimeToReach: '2-3 years',
      },
      {
        title: `Senior ${career.title}`,
        yearsExperience: '5-8 years',
        salaryRange: { min: Math.round(midSenior), max: Math.round(seniorLead) },
        keyResponsibilities: [
          'Lead major initiatives',
          'Define best practices',
          'Strategic decision-making',
          'Cross-functional leadership',
        ],
        skillsRequired: [...career.requiredSkills, 'Leadership', 'Strategic thinking'],
        typicalTimeToReach: '5-6 years',
      },
      {
        title: `Lead/Director`,
        yearsExperience: '8+ years',
        salaryRange: { min: Math.round(seniorLead), max: Math.round(seniorLead * 1.4) },
        keyResponsibilities: [
          'Department leadership',
          'Strategy and vision',
          'Team building',
          'Executive partnerships',
        ],
        skillsRequired: ['Leadership', 'Strategy', 'Business acumen', 'People management'],
        typicalTimeToReach: '8-10 years',
      },
    ],
    alternativePaths: [
      {
        title: 'Management Track',
        description: 'Lead teams and focus on people development',
        fromLevel: 2,
        requirements: ['Leadership skills', 'Communication', 'People development', 'Strategic thinking'],
      },
      {
        title: 'Specialist Track',
        description: 'Become a deep expert in your domain',
        fromLevel: 2,
        requirements: ['Deep expertise', 'Continuous learning', 'Innovation', 'Thought leadership'],
      },
    ],
    keyMilestones: [
      { title: 'First Major Win', timing: 'Year 1', description: 'Achieve a significant accomplishment in your role', icon: '🏆' },
      { title: 'Become Go-To Expert', timing: 'Year 2-3', description: 'Be recognized as knowledgeable in your area', icon: '💡' },
      { title: 'Lead a Team/Project', timing: 'Year 3-5', description: 'Take on leadership responsibilities', icon: '👑' },
      { title: 'Strategic Contribution', timing: 'Year 5+', description: 'Influence direction at a higher level', icon: '🎯' },
    ],
    growthTips: [
      'Seek out challenging assignments that stretch your skills',
      'Build relationships across your organization',
      'Stay current with industry trends and best practices',
      'Document your achievements for performance reviews',
      'Find mentors who can guide your growth',
    ],
  };
}

/**
 * Calculate expected salary at each level
 */
export function calculateSalaryProgression(progression: CareerProgression): { level: string; min: number; max: number }[] {
  return progression.levels.map(level => ({
    level: level.title,
    min: level.salaryRange.min,
    max: level.salaryRange.max,
  }));
}
